/* DSA art. 16 notice-and-action form — the caller POST /api/legal/notice was
 * written for. Public and anonymous: art. 16 gives the right to notify to "any
 * individual or entity", so this page must work for someone who has never heard
 * of us and will never have an account.
 *
 * Four things here are not cosmetic:
 *
 *  1. The form comes FIRST, before the explanations. Someone whose recording was
 *     used without a licence arrived to report it, not to read four paragraphs
 *     about our status under the DSA. The paragraphs sit below, where they answer
 *     the questions that come after sending.
 *
 *  2. Picking the child sexual-offence category visibly relaxes the identity fields.
 *     Art. 16(2)(c) waives them for exactly that case, and someone who does not know
 *     that will abandon the report rather than type their name. The waiver is worth
 *     nothing if it is only in the validator.
 *
 *  3. A 503 is rendered as its own state that never says "received" or "try
 *     again". The endpoint returns that code precisely because nothing was
 *     routed; the art. 16(4)-(5) clocks did not start, and the only honest thing
 *     to show is the address that works without this form.
 *
 *  4. All five sections stay on the page, unfolded, word for word from the locale
 *     (artboard 03c). This is a protected zone: paczka 37 changes the SHAPE of the
 *     page, never the sense of a sentence in it.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { api, ApiError } from '../lib/api'
import { useT } from '../lib/i18n'
import { usePublicPath } from '../lib/publicPaths'
import { useGone } from '../hooks/useGone'
import { scrollToSection, useScrollSpy } from '../lib/useScrollSpy'
import Icon from '../components/Icon'
import LegalShell from '../components/LegalShell'
import DocToc from '../components/public/DocToc'
import { Button, Checkbox, Field, Select } from '../ds'

// Mirrors app/legal/service.py CATEGORIES. `anonymousOk` marks the one category
// where art. 16(2)(c) lifts the identity requirement — if these two lists ever
// disagree, the server wins and the user is told to fill a field the form called
// optional, so they are kept in the same order and named the same way.
//
// WHY THERE ARE TWO SEXUAL-OFFENCE ROWS. The exception in art. 16(2)(c) covers
// only the offences in Articles 3 to 7 of Directive 2011/93/EU, and every one of
// them is against a minor. One combined row promised anonymity for offences
// against adults as well — a false statement about the law in a document we
// publish under a statutory duty.
const CATEGORIES = [
  { value: 'copyright', label: 'catCopyright', anonymousOk: false },
  { value: 'impersonation', label: 'catImpersonation', anonymousOk: false },
  { value: 'personal_data', label: 'catPersonalData', anonymousOk: false },
  { value: 'child_sexual_offence', label: 'catChildSexualOffence', anonymousOk: true },
  { value: 'sexual_offence', label: 'catSexualOffence', anonymousOk: false },
  { value: 'other', label: 'catOther', anonymousOk: false },
] as const

type FieldName = 'location' | 'reason' | 'name' | 'email' | 'good_faith'

const SECTIONS = ['form', 'how', 'who', 'contactPoint', 'abuse'] as const

interface NoticeErrorBody { error?: string; fields?: string[] }

export default function Zgloszenie() {
  const t = useT()
  const pub = usePublicPath()

  const [category, setCategory] = useState<string>('copyright')
  const [location, setLocation] = useState('')
  const [reason, setReason] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [goodFaith, setGoodFaith] = useState(false)

  const [errors, setErrors] = useState<Partial<Record<FieldName, boolean>>>({})
  const [sending, setSending] = useState(false)
  // 'undeliverable' is deliberately a separate state from 'failed': one means the
  // send did not work, the other means nothing will work until an address is
  // configured. Collapsing them would put "try again" in front of a person whose
  // report has nowhere to go.
  const [fatal, setFatal] = useState<'' | 'generic' | 'rateLimited' | 'undeliverable'>('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const anonymousOk = useMemo(
    () => CATEGORIES.find((c) => c.value === category)?.anonymousOk ?? false,
    [category],
  )

  // Same rule as the server's _looks_like_email: permissive on purpose. Rejecting
  // a genuine notice over an address shape is the worse failure.
  const emailLooksValid = (v: string) => {
    if (!v || v.includes(' ')) return false
    const [local, ...rest] = v.split('@')
    const domain = rest.join('@')
    return rest.length === 1 && !!local && domain.includes('.') && !domain.startsWith('.')
  }

  function validate(): Partial<Record<FieldName, boolean>> {
    const next: Partial<Record<FieldName, boolean>> = {}
    if (!location.trim()) next.location = true
    if (!reason.trim()) next.reason = true
    if (!goodFaith) next.good_faith = true
    if (!anonymousOk) {
      if (!name.trim()) next.name = true
      if (!emailLooksValid(email.trim())) next.email = true
    } else if (email.trim() && !emailLooksValid(email.trim())) {
      next.email = true
    }
    return next
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setFatal('')
    const clientErrors = validate()
    setErrors(clientErrors)
    if (Object.keys(clientErrors).length > 0) {
      document.getElementById(`dsa-${Object.keys(clientErrors)[0]}`)?.focus()
      return
    }

    setSending(true)
    try {
      await api('/legal/notice', {
        method: 'POST',
        body: JSON.stringify({
          category,
          location: location.trim(),
          reason: reason.trim(),
          name: name.trim(),
          email: email.trim(),
          good_faith: goodFaith,
        }),
      })
      setSentTo(email.trim() || null)
      setDone(true)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 503) { setFatal('undeliverable'); return }
        if (err.status === 429) { setFatal('rateLimited'); return }
        const body = err.body as NoticeErrorBody | null
        // The server is authoritative about which fields are missing: it, not
        // this component, decides what art. 16(2) requires for a category.
        if (err.status === 400 && Array.isArray(body?.fields) && body.fields.length > 0) {
          const marked: Partial<Record<FieldName, boolean>> = {}
          for (const f of body.fields) marked[f as FieldName] = true
          setErrors(marked)
          document.getElementById(`dsa-${body.fields[0]}`)?.focus()
          return
        }
      }
      setFatal('generic')
    } finally {
      setSending(false)
    }
  }

  function reset() {
    setLocation(''); setReason(''); setName(''); setEmail('')
    setGoodFaith(false); setErrors({}); setFatal(''); setDone(false); setSentTo(null)
  }

  const err = (f: FieldName) => (errors[f] ? t(`legal.zgloszenie.err.${f}`) : undefined)
  // Gwiazdka pola wymaganego jest OZDOBĄ ETYKIETY, nie częścią jej tekstu: `Field`
  // i `Select` przyjmują etykietę jako string, a gwiazdka w środku stringa nie da
  // się pomalować na kolor ostrzeżenia. Klasa `is-required` dokłada ją w CSS.
  const req = (on: boolean) => (on ? 'is-required' : undefined)
  const optional = ` (${t('legal.zgloszenie.form.optional')})`

  const items = SECTIONS.map((s) => ({
    id: s,
    label: t(`legal.zgloszenie.${s === 'form' ? 'form.heading' : `${s}.title`}`),
  }))
  const activeId = useScrollSpy([...SECTIONS])
  // JEDNA wypełniona akcja na ekranie (JEZYK 4): dopóki widać „Wyślij zgłoszenie”, marketingowe
  // „Zacznij za darmo” w nagłówku ma obrys i nie konkuruje z akcją prawną (art. 16 DSA, PROD2
  // F2-15). Obserwowany jest przycisk wysłania formularza; po wysłaniu formularza nie ma.
  const [form, setForm] = useState<HTMLFormElement | null>(null)
  const submitRef = useMemo(() => ({ current: form?.querySelector<HTMLElement>('button[type="submit"]') ?? null }), [form])
  const submitGone = useGone(submitRef, true)

  const shell = (children: React.ReactNode) => (
    <LegalShell tab="zgloszenie" title={t('legal.zgloszenie.title')} quietCta={submitRef.current != null && !submitGone}
                version={t('legal.zgloszenie.sub')} date={t('legal.zgloszenie.date')}>
      <DocToc items={items} activeId={activeId} onSelect={scrollToSection}
              meta={t('legal.toc.sections', { n: items.length })} />
      <div className="mp-legal">{children}</div>
    </LegalShell>
  )

  if (done) {
    // Artboard 03e: jedna karta potwierdzenia, JEDEN przycisk („Wróć na stronę główną”)
    // i cicha droga do kolejnego zgłoszenia pod nim.
    return shell(
      <section id="form">
        <div className="mp-done">
          <span className="mp-done-ic" aria-hidden="true"><Icon name="Check" size={22} strokeWidth={2.5} /></span>
          <h2>{t('legal.zgloszenie.done.title')}</h2>
          <p>
            {sentTo
              ? t('legal.zgloszenie.done.withEmail', { email: sentTo })
              : t('legal.zgloszenie.done.withoutEmail')}
          </p>
          <p>{t('legal.zgloszenie.done.p2')}</p>
          <Button variant="secondary" to={pub()}>{t('legal.zgloszenie.done.back')}</Button>
          <button type="button" className="mn-lnk" onClick={reset}>{t('legal.zgloszenie.done.another')}</button>
        </div>
      </section>,
    )
  }

  return shell(
    <>
      <section id="form">
        {/* Zdanie wprowadzajace art. 16 (S5, slowo w slowo z main): strona jest
            adresowana do osob spoza produktu i to ono mowi im, czego dotyczy formularz
            i ze konto w EDIMI nie jest do tego potrzebne. */}
        <p className="mp-report-lead">{t('legal.zgloszenie.lead')}</p>

        <form className="mp-report" onSubmit={submit} noValidate ref={setForm}>
          {fatal === 'undeliverable' && (
            <p className="mp-errs mp-errs-long" role="alert">{t('legal.zgloszenie.err.undeliverable')}</p>
          )}
          {(fatal === 'generic' || fatal === 'rateLimited') && (
            <p className="mp-errs mp-errs-long" role="alert">{t(`legal.zgloszenie.err.${fatal}`)}</p>
          )}
          {Object.keys(errors).length > 0 && (
            <p className="mp-errs" role="alert">{t('legal.zgloszenie.err.heading')}</p>
          )}

          <p className="mp-report-legend">{t('legal.zgloszenie.form.note')}</p>

          {/* DS Select, not a native one: this page is the first thing a stranger sees
              of us, and a system dropdown inside a styled form reads as unfinished —
              a poor look on the page where somebody reports illegal content. */}
          <Select
            id="dsa-category"
            className={req(true)}
            label={t('legal.zgloszenie.form.category')}
            value={category}
            onChange={(v) => { setCategory(v); setErrors({}) }}
            options={CATEGORIES.map((c) => ({
              value: c.value,
              label: t(`legal.zgloszenie.form.${c.label}`),
            }))}
          />

          {anonymousOk && (
            <p className="mp-anon">{t('legal.zgloszenie.form.anonymousNote')}</p>
          )}

          <Field
            id="dsa-location"
            type="text"
            className={req(true)}
            label={t('legal.zgloszenie.form.location')}
            value={location}
            placeholder={t('legal.zgloszenie.form.locationPlaceholder')}
            hint={t('legal.zgloszenie.form.locationHint')}
            error={err('location')}
            onChange={(e) => setLocation(e.target.value)}
          />

          <Field
            multiline
            id="dsa-reason"
            rows={4}
            className={req(true)}
            label={t('legal.zgloszenie.form.reason')}
            value={reason}
            hint={t('legal.zgloszenie.form.reasonHint')}
            error={err('reason')}
            onChange={(e) => setReason(e.target.value)}
          />

          <Field
            id="dsa-name"
            type="text"
            className={req(!anonymousOk)}
            label={t('legal.zgloszenie.form.name') + (anonymousOk ? optional : '')}
            value={name}
            error={err('name')}
            onChange={(e) => setName(e.target.value)}
          />

          <Field
            id="dsa-email"
            type="email"
            className={req(!anonymousOk)}
            label={t('legal.zgloszenie.form.email') + (anonymousOk ? optional : '')}
            value={email}
            placeholder={t('auth.login.emailPlaceholder')}
            hint={t('legal.zgloszenie.form.emailHint')}
            error={err('email')}
            onChange={(e) => setEmail(e.target.value)}
          />

          {/* art. 16(2)(d): the declaration is the notice's legal backbone, so it gets
              the whole sentence as a click target rather than a 24px box.
              Gwiazdka stoi W ETYKIECIE, jak na main (S4): regula `.mp-report
              .is-required > label::after` celuje w bezposrednie dziecko, a `<label>`
              Checkboxa siedzi w `.ds-check-row`, wiec klasa nigdy by tu nie zadzialala.
              W tekscie etykiety gwiazdka dociera tez do czytnika ekranu. */}
          <Checkbox
            id="dsa-good_faith"
            checked={goodFaith}
            onChange={(e) => setGoodFaith(e.target.checked)}
            label={`${t('legal.zgloszenie.form.goodFaith')} *`}
            error={errors.good_faith ? t('legal.zgloszenie.err.good_faith') : undefined}
          />

          <p className="mp-report-privacy">
            {t('legal.zgloszenie.form.privacyNote')}{' '}
            <Link to={pub('/polityka')}>{t('legal.zgloszenie.form.privacyLink')}</Link>
          </p>

          <Button type="submit" variant="primary" busy={sending}>{t('legal.zgloszenie.form.submit')}</Button>
        </form>
      </section>

      <section id="how">
        <h2>{t('legal.zgloszenie.how.title')}</h2>
        <ol>
          <li>{t('legal.zgloszenie.how.s1')}</li>
          <li>{t('legal.zgloszenie.how.s2')}</li>
          <li>{t('legal.zgloszenie.how.s3')}</li>
          <li>{t('legal.zgloszenie.how.s4')}</li>
        </ol>
      </section>

      <section id="who">
        <h2>{t('legal.zgloszenie.who.title')}</h2>
        <p>{t('legal.zgloszenie.who.p1')}</p>
        <p>{t('legal.zgloszenie.who.p2')}</p>
        <p>{t('legal.zgloszenie.who.p3')}</p>
        {/* Invariant #8, stated to the notifier rather than only in CLAUDE.md:
            it is the fact that makes p2's first ground true, so the person
            reading why we are not an online platform can see it. */}
        <p data-testid="dsa-no-public-sharing">{t('legal.zgloszenie.who.p4')}</p>
      </section>

      <section id="contactPoint">
        <h2>{t('legal.zgloszenie.contactPoint.title')}</h2>
        <p>{t('legal.zgloszenie.contactPoint.p1')}</p>
        <p>{t('legal.zgloszenie.contactPoint.p2')}</p>
      </section>

      <section id="abuse">
        <h2>{t('legal.zgloszenie.abuse.title')}</h2>
        <p>{t('legal.zgloszenie.abuse.p1')}</p>
      </section>
    </>,
  )
}
